import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Icons } from '@/src/modules/components/Icons';
import { Footer } from '@/src/modules/components/Footer';

interface ContactItem {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}

export function Company() {

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'about';

      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="flex flex-col w-full bg-surface-lowest min-h-screen">

      <div className="flex-1">
        {/* About Section */}
        <section id="about" className="py-24 px-6 border-b border-outline-variant/20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-on-surface mb-6">About InviteCloud</h2>
            <div className="space-y-4 text-on-surface-variant leading-relaxed">
              <p>
                InviteCloud is an <strong>AI-native invitation platform</strong> designed to make creating and sharing digital invitations, video experience templates, and guest RSVPs effortless and extraordinary. Founded in June 2026, we believe every celebration deserves an invitation as memorable as the event itself.
              </p>
              <p>
                Our mission is to pioneer conversational, multi-agent AI event creation. We empower hosts, event planners, and creative partners to generate custom, web-ready invitation sites within minutes simply by describing their event details through natural language.
              </p>
              <p>
                Built for the modern AI ecosystem, InviteCloud seamlessly connects across <strong>ChatGPT Apps</strong>, <strong>Claude Code</strong>, and our custom <strong>Model Context Protocol (MCP) Server</strong> — enabling AI agents and users alike to design, customize, and publish personalized wedding, birthday, and celebration experiences in real time.
              </p>
              <p>
                For hosts planning one big day, and partners planning hundreds. From intimate celebrations to large-scale cultural events, InviteCloud merges state-of-the-art AI generation with cinematic experience design.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Privacy Policy Section */}
        <section id="privacy-policy" className="py-24 px-6 border-b border-outline-variant/20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-on-surface mb-6">Privacy Policy</h2>
            <div className="space-y-6 text-on-surface-variant leading-relaxed">
              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">1. Introduction</h3>
                <p>
                  InviteCloud ("we", "us", "our", or "Company") operates the InviteCloud website and platform. This page
                  informs you of our policies regarding the collection, use, and disclosure of personal data when you use
                  our service and the choices you have associated with that data.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">2. Information Collection and Use</h3>
                <p>We collect several different types of information for various purposes to provide and improve our service:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Account information (name, email, phone)</li>
                  <li>Invitation content and guest information</li>
                  <li>Payment information (processed securely through third-party providers)</li>
                  <li>Usage data and analytics</li>
                  <li>Device information and browser data</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">3. Data Security</h3>
                <p>
                  The security of your data is important to us but remember that no method of transmission over the Internet
                  or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to
                  protect your personal data, we cannot guarantee its absolute security.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">4. Changes to This Privacy Policy</h3>
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new
                  Privacy Policy on this page and updating the "effective date" at the top of this Privacy Policy.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">5. Contact Us</h3>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at{' '}
                  <a href="mailto:support@invitecloud.in" className="text-primary hover:underline">support@invitecloud.in</a> or{' '}
                  <a href="mailto:founder@invitecloud.in" className="text-primary hover:underline">founder@invitecloud.in</a>.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Terms of Service Section */}
        <section id="terms" className="py-24 px-6 border-b border-outline-variant/20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-on-surface mb-6">Terms of Service</h2>
            <div className="space-y-6 text-on-surface-variant leading-relaxed">
              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">1. Agreement to Terms</h3>
                <p>
                  By accessing and using InviteCloud, you accept and agree to be bound by the terms and provision of this
                  agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">2. Use License</h3>
                <p>
                  Permission is granted to temporarily download one copy of the materials (information or software) on
                  InviteCloud for personal, non-commercial transitory viewing only. This is the grant of a license, not a
                  transfer of title, and under this license you may not:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Modifying or copying the materials</li>
                  <li>Using the materials for any commercial purpose</li>
                  <li>Attempting to decompile or reverse engineer any software contained on InviteCloud</li>
                  <li>Removing any copyright or other proprietary notations from the materials</li>
                  <li>Transferring the materials to another person or "mirroring" the materials on any other server</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">3. Disclaimer</h3>
                <p>
                  The materials on InviteCloud are provided on an 'as is' basis. InviteCloud makes no warranties, expressed
                  or implied, and hereby disclaims and negates all other warranties including, without limitation, implied
                  warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of
                  intellectual property or other violation of rights.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-on-surface mb-2">4. Limitations</h3>
                <p>
                  In no event shall InviteCloud or its suppliers be liable for any damages (including, without limitation,
                  damages for loss of data or profit, or due to business interruption) arising out of the use or inability
                  to use the materials on InviteCloud.
                </p>
              </div>
            </div>
          </motion.div>
        </section>


        {/* Contact Section */}
        <section id="contact" className="py-24 px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold text-on-surface mb-6">Get in Touch</h2>
            <p className="text-lg text-on-surface-variant mb-8">
              Have questions? We'd love to hear from you. Contact us anytime.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-xl mx-auto text-left">
              <div className="p-6 rounded-2xl bg-surface border border-outline-variant/30 flex flex-col gap-2">
                <span className="text-sm font-semibold text-primary">Founder & Executive</span>
                <a href="mailto:founder@invitecloud.in" className="text-base font-bold text-on-surface hover:text-primary transition-colors">
                  founder@invitecloud.in
                </a>
                <p className="text-xs text-on-surface-variant">For partnerships, strategic inquiries, and executive support.</p>
              </div>

              <div className="p-6 rounded-2xl bg-surface border border-outline-variant/30 flex flex-col gap-2">
                <span className="text-sm font-semibold text-primary">Customer Support</span>
                <a href="mailto:support@invitecloud.in" className="text-base font-bold text-on-surface hover:text-primary transition-colors">
                  support@invitecloud.in
                </a>
                <p className="text-xs text-on-surface-variant">For platform help, billing, and invitation support.</p>
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to="/dashboard/help"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors font-semibold shadow-md"
              >
                <Icons.Help size={20} />
                Help & Support Dashboard
              </Link>
            </div>
          </motion.div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
